/**
 * TenantFormModal Component
 *
 * A modal wrapper for the tenant create/edit form.
 * Handles both create and edit modes with appropriate titles and actions.
 *
 * @module features/tenants/components/modals/TenantFormModal
 */

import { AlertIcon } from '../../../../components/common/Icons';
import type { Tenant } from '../../types';

interface TenantFormModalProps {
  /** Modal mode */
  mode: 'create' | 'edit';
  /** Whether the modal is open */
  isOpen: boolean;
  /** The tenant being edited (for edit mode) */
  tenant: Tenant | null;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback to submit the form */
  onSubmit: () => void;
  /** Whether the form is currently saving */
  isSaving: boolean;
  /** Error message to display */
  error: string | null;
  /** Callback to clear error */
  onClearError: () => void;
  /** The form content to render */
  children: React.ReactNode;
}

/**
 * Renders a modal for creating or editing a tenant.
 */
export const TenantFormModal: React.FC<TenantFormModalProps> = ({
  mode,
  isOpen,
  tenant,
  onClose,
  onSubmit,
  isSaving,
  error,
  onClearError,
  children,
}) => {
  if (!isOpen) {
    return null;
  }

  // For edit mode, require tenant
  if (mode === 'edit' && !tenant) {
    return null;
  }

  const title = mode === 'create'
    ? 'Create New Tenant'
    : `Edit Tenant: ${tenant?.name}`;

  const submitLabel = mode === 'create'
    ? (isSaving ? 'Creating...' : 'Create Tenant')
    : (isSaving ? 'Saving...' : 'Save Changes');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-lg"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '640px', maxHeight: '80vh', overflow: 'auto' }}
      >
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="btn btn-ghost" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {/* Modal Error Display */}
          {error && (
            <div className="alert alert-error" style={{
              marginBottom: '16px',
              padding: '12px 16px',
              borderRadius: '8px',
              background: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.5)',
              color: 'var(--error)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertIcon size={18} />
              <span style={{ flex: 1 }}>{error}</span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={onClearError}
                style={{ padding: '4px', minWidth: 'auto' }}
              >
                ×
              </button>
            </div>
          )}
          {children}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={isSaving}
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
